import React from 'react';
import styled from 'styled-components';
import {ExpandableIcon} from '@/web-components';
import {BUDGET_TYPE, PERIOD_BUDGET_TYPE} from '../../../../../constants';
import {isBillableSplitAllowed} from '../../../../shared/util/cache/TimeRegistrationSettingsUtil';
import {hasFeatureFlag} from '../../../../shared/util/FeatureUtil';
import {isClientUser, showRevenue as canViewRevenue, showCost as canViewCost} from '../../../../shared/util/PermissionsUtil';
import ProjectUtil from '../../../../shared/util/ProjectUtil';
import {Element, GridContainer, Header, IconWrapper, Row, TotalsBox} from './ProjectScopingTotals_styled';
import {useProjectTotals} from './ProjectScopingTotalsContext';
import {AvailabilityTile} from './totals-header-box-tiles/AvailabilityTile';
import {DatesAndRevenueTile} from './totals-header-box-tiles/DatesAndRevenueTile';
import {EstimateAndTimeTile} from './totals-header-box-tiles/EstimateAndTimeTile';
import {RemainingAndBaselineTile} from './totals-header-box-tiles/RemainingAndBaselineTile';
import {TileVariant} from './totals-header-box-tiles/TotalsTypes';
import {FixedPriceLeftTile} from './totals-header-box-tiles/FixedPriceLeftTile';
import {FixedPriceMiddleTile} from './totals-header-box-tiles/FixedPriceMiddleTile';
import {FixedPriceRightTile} from './totals-header-box-tiles/FixedPriceRightTile';
import {Tile} from './totals-header-box-tiles/Tile_styled';
import {ProjectScopingTotalsRows as TotalsRows} from './ProjectScopingTotalsRows';

const HeaderTotalsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
	grid-gap: 24px 48px;
	padding-right: 36px;
`;

// Attempt to fit the availability tile into the secondary row of one of the other tiles instead of showing it as its own standalone tile if there is space for it.
const getAvailabilityTile = (
	showRevenue: boolean,
	showBillableSplit: boolean,
	showBaseline: boolean,
	showAvailabilityOnScoping: boolean
): TileVariant | null => {
	if (!showAvailabilityOnScoping) return null;

	// If none of the other secondary rows are shown, show as standalone column
	if (!showRevenue && !showBillableSplit && !showBaseline) return TileVariant.Availability;

	if (!showRevenue) return TileVariant.DatesAndRevenue;

	if (!showBillableSplit) return TileVariant.EstimateAndTime;

	if (!showBaseline) return TileVariant.RemainingAndBaseline;

	return TileVariant.Availability;
};

/**
 * Create the project scoping header section in a big box (Expanded)
 *
 * @returns JSX Project Scoping Totals Box Component
 */
export const ProjectScopingTotalsBox = (): JSX.Element => {
	const {totalsExpanded, toggleTotalsExpanded, totalsData, settings, projectData} = useProjectTotals();

	const hasFinancialServiceInScoping = hasFeatureFlag('use_financial_service_in_scoping');
	const hasFinancialCategoriesUpdate = hasFeatureFlag('financial_categories_update');

	const projectTracksRevenue = ProjectUtil.projectTracksRevenue(projectData);
	const isNonBillable = !projectTracksRevenue;

	const isFixedPriceBudgetType = projectData.budgetType === BUDGET_TYPE.FIXED_PRICE_V2;
	const isFixedPriceV2 =
		projectTracksRevenue &&
		(isFixedPriceBudgetType ||
			(projectData.budgetType === BUDGET_TYPE.RETAINER &&
				projectData.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE));

	const showBillableSplit = !isNonBillable && isBillableSplitAllowed();
	const showBaseline = !!settings.isBaselineProject;
	const showAvailabilityOnScoping = !!settings.isUsingProjectAllocation && !isClientUser();

	const showRevenue = projectTracksRevenue && canViewRevenue();
	const showCost = canViewCost();

	const availabilityTile = getAvailabilityTile(showRevenue, showBillableSplit, showBaseline, showAvailabilityOnScoping);
	const showAvailabilityTile = availabilityTile === TileVariant.DatesAndRevenue;

	return (
		<GridContainer data-userpilot="project-totals-header">
			<TotalsBox key="dates-revenue-box" data-cy="project-totals-header">
				<Row bottomMargin={16}>
					<Element split>
						<Header>Project totals</Header>
					</Element>
					<Element alignRight>
						<IconWrapper>
							<ExpandableIcon
								isOpen={totalsExpanded}
								onClick={toggleTotalsExpanded}
								cy={'collapse-project-totals'}
							/>
						</IconWrapper>
					</Element>
				</Row>
				<HeaderTotalsGrid>
					{hasFinancialServiceInScoping && hasFinancialCategoriesUpdate ? (
						isFixedPriceV2 ? (
							<>
								<FixedPriceLeftTile />
								<FixedPriceMiddleTile />
								<FixedPriceRightTile />
							</>
						) : isNonBillable ? (
							totalsData.isEstimatedInHours ? (
								<>
									<Tile>
										<TotalsRows.PlannedHours />
										<TotalsRows.ActualHours />
									</Tile>
									<Tile>
										<TotalsRows.RemainingWorkHours />
										<TotalsRows.ProjectedTotalHours />
									</Tile>
									{showCost && (
										<Tile>
											<TotalsRows.PlannedCost />
											<TotalsRows.ActualCost />
										</Tile>
									)}
									<Tile>
										{showCost && <TotalsRows.ProjectedTotalCost />}
										<TotalsRows.Progress />
									</Tile>
								</>
							) : (
								<>
									<Tile>
										<TotalsRows.ActualHours />
										<TotalsRows.PlannedHours />
										<TotalsRows.RemainingWorkHours />
										<TotalsRows.ProjectedTotalHours />
									</Tile>
									<Tile>
										<TotalsRows.Progress />
										<TotalsRows.EstimatedPoints />
										<TotalsRows.CompletedPoints />
										<TotalsRows.RemainingPoints />
									</Tile>
									{showCost && (
										<Tile>
											<TotalsRows.PlannedCost />
											<TotalsRows.ActualCost />
											<TotalsRows.RemainingCost />
											<TotalsRows.ProjectedTotalCost />
										</Tile>
									)}
								</>
							)
						) : (
							<>
								{/* T&M, Fixed Hours and T&M retainers */}
								<Tile>
									<TotalsRows.PlannedHours />
									<TotalsRows.ActualHours />
									<TotalsRows.RemainingWorkHours />
									<TotalsRows.ProjectedTotalHours />
								</Tile>
								<Tile>
									<TotalsRows.Progress />
									{totalsData.isEstimatedInHours ? (
										<>
											<TotalsRows.ActualNonBillableHours />
											<TotalsRows.ProjectedTotalNonBillableHours />
											<TotalsRows.ProjectedTotalHoursPercentageBillable />
										</>
									) : (
										<>
											<TotalsRows.EstimatedPoints />
											<TotalsRows.CompletedPoints />
											<TotalsRows.RemainingPoints />
										</>
									)}
								</Tile>
								{showRevenue && (
									<>
										<Tile>
											<TotalsRows.PlannedRevenue />
											{showCost && <TotalsRows.PlannedCost />}
											{showCost && <TotalsRows.PlannedProfit />}
											{showCost && <TotalsRows.PlannedMargin />}
										</Tile>
										<Tile>
											<TotalsRows.ProjectedTotalRevenue />
											{showCost && <TotalsRows.ProjectedTotalCost />}
											{showCost && <TotalsRows.ProjectedTotalProfit />}
											{showCost && <TotalsRows.ProjectedTotalMargin />}
										</Tile>
									</>
								)}
							</>
						)
					) : (
						<>
							<DatesAndRevenueTile
								showRevenue={showRevenue}
								isFixedPriceV2={isFixedPriceV2}
								hasFinancialServiceInScoping={hasFinancialServiceInScoping}
								showAvailabilityTile={showAvailabilityTile}
							/>
							<EstimateAndTimeTile
								showBillableSplit={showBillableSplit}
								showAvailabilityTile={showAvailabilityTile}
							/>
							<RemainingAndBaselineTile
								showRevenue={showRevenue}
								showBaseline={showBaseline}
								showAvailabilityTile={showAvailabilityTile}
							/>
							{availabilityTile === TileVariant.Availability && <AvailabilityTile />}
						</>
					)}
				</HeaderTotalsGrid>
			</TotalsBox>
		</GridContainer>
	);
};
